import React from 'react';
import SecondaryButton from '@/components/common/buttons/SecondaryButton';
import { Box, Flex, VStack } from '@chakra-ui/react';
import { DocumentWithDownloadUrl as Document } from '@/client';
import BaseDocumentEdit from './BaseDocumentEdit';
import CriticalInfoContent from '@/components/on-boarding/steps/critical-info/CriticalInfoContent';
import { useCriticalInfoDocument } from '@/hooks/house/critical-info/useCriticalInfoDocument';
import { toaster } from '@/components/ui/toaster';
import { Loading } from '@/components/common/Loading.tsx';
import { useImportantSystemsPermissions } from '@/hooks/house/critical-info/useImportantSystemsPermissions';
import { getDocumentedSystemNames } from '@/utils/criticalInfoUtils';

interface CriticalInfoEditProps {
	document: Document;
	onDocumentUpdated: () => void;
	onVisibilityChange?: (isPrivate: boolean) => void;
}

/**
 * Component for editing important systems documents
 * Uses useCriticalInfoDocument hook with internal update button
 */
const CriticalInfoEdit: React.FC<CriticalInfoEditProps> = (props) => {
	const { document, onDocumentUpdated, onVisibilityChange } = props;
	const { canEdit, canRemove } = useImportantSystemsPermissions();
	const {
		systems,
		isLoading,
		error,
		addSystem,
		removeSystem,
		updateSystemField,
		saveCriticalInfo,
		isSaving,
		criticalInfoData,
	} = useCriticalInfoDocument(document.house_id);
	const savedDocumentedSystemNames = getDocumentedSystemNames(criticalInfoData?.systems ?? []);

	const handleSaveCriticalInfo = async () => {
		if (!canEdit) return;

		if (isSaving) {
			toaster.create({
				description: 'Please wait while saving...',
				type: 'warning',
				closable: true,
			});
			return;
		}

		try {
			await saveCriticalInfo(systems);
			toaster.create({
				description: 'Important systems updated successfully!',
				type: 'success',
				closable: true,
			});
			onDocumentUpdated();
		} catch (error) {
			console.error('Failed to save important systems:', error);
			toaster.create({
				title: 'Error',
				description: 'Failed to update important systems',
				type: 'error',
				closable: true,
			});
		}
	};

	// Determine if the update button should be enabled
	const isUpdateEnabled = canEdit && systems.length > 0 && !isLoading && !isSaving;

	return (
		<BaseDocumentEdit document={document} onVisibilityChange={onVisibilityChange}>
			<VStack gap={4} align="stretch">
				<Box position="relative">
					{error ? (
						<Box p={4} bg="red.50" color="text.error" borderRadius="md">
							Error loading important systems
						</Box>
					) : isLoading ? (
						<Loading text="Loading important systems..." />
					) : systems.length === 0 ? (
						<Box p={4} bg="gray.50" color="fg.muted" borderRadius="md">
							No important systems available
						</Box>
					) : (
						<CriticalInfoContent
							systems={systems}
							isLoading={isLoading}
							addSystem={addSystem}
							removeSystem={removeSystem}
							updateSystemField={updateSystemField}
							documentedSystemNames={savedDocumentedSystemNames}
							canEdit={canEdit}
							canRemove={canRemove}
						/>
					)}
					{canEdit && systems.length > 0 && (
						<Flex w="100%" justify="flex-end" mt={4}>
							<SecondaryButton
								onClick={handleSaveCriticalInfo}
								loading={isSaving}
								loadingText="Updating..."
								disabled={!isUpdateEnabled}
							>
								Update
							</SecondaryButton>
						</Flex>
					)}
				</Box>
			</VStack>
		</BaseDocumentEdit>
	);
};

export default CriticalInfoEdit;
